import { meetsMinPlan } from './plans';
import type { PlanTier } from './auth';
// ── CRM & Loyalty Types ────────────────────────────────────────────────────

/** Guest segment auto-calculated by cron */
export type GuestSegment = 'new' | 'regular' | 'vip' | 'at_risk' | 'dormant' | 'lost';

/** Loyalty tier based on points accumulated */
export type LoyaltyTier = 'bronze' | 'silver' | 'gold' | 'platinum';

/** Consent type for PDPA/GDPR compliance */
export type ConsentType = 'loyalty_enrollment' | 'marketing_whatsapp' | 'marketing_sms' | 'marketing_email' | 'data_collection' | 'data_sharing';

/** Source of consent capture */
export type ConsentSource = 'checkout_form' | 'booking_form' | 'guest_app_settings' | 'qr_scan' | 'staff_entry' | 'api';

// ── Guest Profile ──────────────────────────────────────────────────────────

export interface GuestProfile {
  id: string;
  organizationId: string;
  phone: string | null;
  email: string | null;
  name: string | null;
  /** Linked guest ACCOUNT (auth user) when the guest ordered while logged in. */
  customerUserId?: string | null;
  birthday: string | null;
  gender: string | null;
  languagePreference: string;
  // Stats
  totalOrders: number;
  totalSpent: number;
  averageOrderValue: number;
  firstOrderAt: string | null;
  lastOrderAt: string | null;
  visitCount: number;
  // Loyalty
  loyaltyPointsBalance: number;
  loyaltyTier: LoyaltyTier;
  loyaltyEnrolledAt: string | null;
  // Preferences
  favoriteItems: Array<{ itemId: string; itemName: string; orderCount: number }>;
  dietaryPreferences: string[];
  notes: string | null;
  pinnedNote: string | null;
  tags: string[];
  /** Owner-controlled VIP flag. Independent of `segment` (which auto-recomputes) — the VIP tab filters on this. */
  isVip: boolean;
  // Segment
  segment: GuestSegment;
  segmentUpdatedAt: string;
  // Communication
  whatsappOptIn: boolean;
  smsOptIn: boolean;
  emailOptIn: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── Guest Timeline Event ───────────────────────────────────────────────────

export type TimelineEventType = 'order' | 'booking' | 'points_earned' | 'points_redeemed' | 'tier_upgrade' | 'review' | 'campaign' | 'tag_change' | 'note';

export interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  title: string;
  description: string | null;
  amount: number | null;
  points: number | null;
  createdAt: string;
  metadata: Record<string, unknown>;
}

// ── Consent Record ─────────────────────────────────────────────────────────

export interface GuestConsentRecord {
  id: string;
  guestProfileId: string;
  consentType: ConsentType;
  isGranted: boolean;
  grantedAt: string | null;
  withdrawnAt: string | null;
  source: ConsentSource;
  createdAt: string;
}

// ── CRM Stats ──────────────────────────────────────────────────────────────

export interface CrmStats {
  totalGuests: number;
  activeGuests: number;
  atRiskGuests: number;
  averageOrderValue: number;
  segmentCounts: Record<GuestSegment, number>;
  tierCounts: Record<LoyaltyTier, number>;
}

// ── Segment Criteria ───────────────────────────────────────────────────────

export const SEGMENT_CRITERIA: Record<GuestSegment, string> = {
  new: 'First order in last 30 days',
  regular: '3+ orders in last 60 days',
  vip: 'Top 10% by spend',
  at_risk: 'No order in 30-60 days (was regular)',
  dormant: 'No order in 60+ days',
  lost: 'No order in 120+ days',
};

// ── Loyalty Program ────────────────────────────────────────────────────────

export type LoyaltyProgramType = 'points' | 'visits' | 'tiers' | 'cashback';
export type LoyaltyTransactionType = 'earn_order' | 'earn_bonus' | 'earn_referral' | 'earn_review' | 'earn_booking' | 'redeem' | 'expire' | 'adjust' | 'refund';
export type RewardType = 'discount_amount' | 'discount_percentage' | 'free_item' | 'free_delivery' | 'cashback';

export interface LoyaltyProgram {
  id: string;
  restaurantId: string;
  isEnabled: boolean;
  programType: LoyaltyProgramType;
  pointsPerCurrency: number;
  currencyPerPoint: number;
  stampsForReward: number;
  silverThreshold: number;
  goldThreshold: number;
  platinumThreshold: number;
  birthdayMultiplier: number;
  firstOrderBonus: number;
  reviewBonus: number;
  bookingBonus: number;
  referralBonus: number;
  pointsExpiryMonths: number;
  minOrderForRedemption: number;
  maxRedemptionPercentage: number;
  createdAt: string;
  updatedAt: string;
}

export interface LoyaltyReward {
  id: string;
  restaurantId: string;
  name: string;
  description: string | null;
  pointsCost: number;
  rewardType: RewardType;
  rewardValue: number;
  freeItemId: string | null;
  minOrderValue: number;
  maxRedemptionsPerGuest: number | null;
  isActive: boolean;
  validFrom: string | null;
  validUntil: string | null;
  noStacking: boolean;
  blackoutDates: Array<{ from: string; to: string }>;
  eligibleCategoryIds: string[] | null;
  excludedItemIds: string[] | null;
  timeRestriction: { days: string[]; startHour: number; endHour: number } | null;
  channelRestriction: string[] | null;
  firstTimeOnly: boolean;
  singleUse: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoyaltyTransaction {
  id: string;
  guestProfileId: string;
  restaurantId: string;
  transactionType: LoyaltyTransactionType;
  points: number;
  balanceAfter: number;
  orderId: string | null;
  rewardId: string | null;
  description: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface LoyaltyStats {
  totalPointsIssued: number;
  totalPointsRedeemed: number;
  totalPointsExpired: number;
  redemptionRate: number;
  pointsLiability: number;
  enrolledGuests: number;
}

// ── CRM Campaigns ──────────────────────────────────────────────────────────

export type CampaignType = 'manual' | 'scheduled' | 'recurring' | 'automated';
export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'active';
export type CampaignChannel = 'whatsapp' | 'sms' | 'email';
export type CampaignTrigger = 'first_order' | 'no_order_14d' | 'no_order_30d' | 'birthday_7d' | 'points_expiring_30d' | 'tier_upgrade' | 'review_request';

export interface CrmCampaign {
  id: string;
  restaurantId: string;
  name: string;
  campaignType: CampaignType;
  segmentFilter: string | null;
  tagFilter: string[];
  channel: CampaignChannel;
  templateName: string | null;
  customMessage: string | null;
  offerCode: string | null;
  offerDiscountType: 'percentage' | 'amount' | null;
  offerDiscountValue: number | null;
  offerMinOrder: number | null;
  offerMaxUses: number | null;
  offerValidFrom: string | null;
  offerValidUntil: string | null;
  status: CampaignStatus;
  scheduledAt: string | null;
  sentAt: string | null;
  recurringDay: string | null;
  recurringTime: string | null;
  triggerEvent: CampaignTrigger | null;
  totalRecipients: number;
  totalSent: number;
  totalDelivered: number;
  totalFailed: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const CAMPAIGN_TRIGGERS: { id: CampaignTrigger; label: string; description: string }[] = [
  { id: 'first_order', label: 'Welcome', description: 'After first order — welcome + loyalty signup' },
  { id: 'no_order_14d', label: 'Win-Back (14d)', description: 'No order in 14 days — "We miss you"' },
  { id: 'no_order_30d', label: 'Win-Back (30d)', description: 'No order in 30 days — stronger offer' },
  { id: 'birthday_7d', label: 'Birthday', description: '7 days before birthday — birthday reward' },
  { id: 'points_expiring_30d', label: 'Points Expiring', description: '30 days before points expire' },
  { id: 'tier_upgrade', label: 'Tier Upgrade', description: 'Congratulations on tier promotion' },
  { id: 'review_request', label: 'Review Request', description: '2 hours after order — ask for review' },
];

// ── Identity Merge + Compliance ────────────────────────────────────────────

export interface GuestDuplicateFlag {
  id: string;
  organizationId: string;
  profileAId: string;
  profileBId: string;
  reason: string;
  confidence: number;
  status: 'pending' | 'merged' | 'dismissed';
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
  // Enriched (joined)
  profileA?: GuestProfile;
  profileB?: GuestProfile;
}

export interface GuestMergeResult {
  survivingProfileId: string;
  mergedProfileId: string;
  combinedOrders: number;
  combinedPoints: number;
  resultTier: LoyaltyTier;
}

export interface GuestDataExport {
  profile: GuestProfile;
  orders: Array<{ id: string; orderNumber: number; totalAmount: number; createdAt: string }>;
  loyaltyTransactions: Array<{ type: string; points: number; createdAt: string }>;
  consentRecords: GuestConsentRecord[];
}

export interface GuestDataRequest {
  id: string;
  guestProfileId: string;
  requestType: 'export' | 'deletion';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  exportUrl: string | null;
  completedAt: string | null;
  requestedVia: string;
  createdAt: string;
}

// ── Plan gating ────────────────────────────────────────────────────────────

/**
 * CRM is Scale and up. Same fix as accounting: the list left HOTEL out, so a hotel had
 * no customer database on a plan that ranks with Scale.
 */
export const CRM_MIN_PLAN: PlanTier = 'scale';

export function isCrmEligible(plan: string): boolean {
  return meetsMinPlan(plan as PlanTier, CRM_MIN_PLAN);
}
